import { convexClient } from "../config/convex";
import { api } from "../../convex/_generated/api";
import type { Doc } from "../../convex/_generated/dataModel";
import type {
  Expediente,
  HistorialCambio,
  ComunicacionInterna,
} from "./expedienteService";
export interface EstadisticasDashboard {
  total: number;
  enTramite: number;
  rechazadas: number;
  resueltas: number;
  arquitectonicas: number;
  urbanisticas: number;
  transporte: number;
  tiposBarrera: Record<string, number>;
  departamentos: Record<string, number>;
}

export interface FiltrosExpedientes {
  busqueda?: string;
  estado?: string;
  tipoBarrera?: string;
  departamento?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  direccion?: string;
  comunicacionInterna?: string;
  ordenarPor?: "fecha_desc" | "fecha_asc" | "id_asc" | "id_desc";
}
import { cacheService } from "./cacheService";

type ExpedienteDoc = Doc<"expedientes">;

function toExpediente(
  doc: ExpedienteDoc | null,
  historial: { fecha: string; usuario: string; cambio: string; informacion_denunciante?: string }[],
  comunicaciones: { fecha: string; usuario: string; mensaje: string }[]
): Expediente | null {
  if (!doc) return null;
  const h: HistorialCambio[] = historial.map((x) => ({
    fecha: x.fecha,
    usuario: x.usuario,
    cambio: x.cambio,
    ...(x.informacion_denunciante ? { informacionDenunciante: x.informacion_denunciante } : {}),
  }));
  const c: ComunicacionInterna[] = comunicaciones.map((x) => ({
    fecha: x.fecha,
    usuario: x.usuario,
    mensaje: x.mensaje,
  }));
  return {
    id: doc.id,
    estado: doc.estado ?? "En trámite",
    fechaCreacion: doc.fecha_creacion,
    tipoBarrera: doc.tipo_barrera ?? "",
    tipoBarreraDetalle: doc.tipo_barrera_detalle ?? "",
    ubicacionBarrera: doc.ubicacion_barrera ?? "",
    email: doc.email ?? "",
    Departamento: (doc.departamento ?? "Administrativo") as
      | "Arquitecto"
      | "Jurídico"
      | "Administrativo",
    nombreApellidos: doc.nombre_apellidos ?? "",
    telefonoFijo: doc.telefono_fijo ?? "",
    movil: doc.movil ?? "",
    descripcion: doc.descripcion ?? "",
    historial: h,
    comunicacionesInternas: c,
    imagenUrl: doc.imagen_url ?? "",
    gradoDiscapacidad: doc.grado_discapacidad ?? "",
    tipoDiscapacidad: doc.tipo_discapacidad ?? "",
    direccion: doc.direccion ?? "",
    localidad: doc.localidad ?? "",
    codigoPostal: doc.codigo_postal ?? "",
    provincia: doc.provincia ?? "",
    documentoIdentidad: doc.documento_identidad ?? "",
    fechaNacimiento: doc.fecha_nacimiento ?? "",
    genero: doc.genero ?? "",
    baremoMovilidad: doc.baremo_movilidad ?? "",
    tipoPersona: (doc.tipo_persona as Expediente["tipoPersona"]) ?? "",
    porcentajeDiscapacidad: doc.porcentaje_discapacidad ?? "",
    imagenBarrera: doc.imagen_barrera ?? "",
  };
}

function docToExpediente(
  doc: ExpedienteDoc,
  historial: { fecha: string; usuario: string; cambio: string; informacion_denunciante?: string }[],
  comunicaciones: { fecha: string; usuario: string; mensaje: string }[] = []
): Expediente {
  return toExpediente(doc, historial, comunicaciones)!;
}

export async function consultarExpediente(numeroExpediente: string): Promise<Expediente | null> {
  try {
    const doc = await convexClient.query(api.expedientes.getById, {
      id: numeroExpediente,
    });
    if (!doc) return null;
    const historial = await convexClient.query(api.historialCambios.listByExpedienteId, {
      expediente_id: doc.id,
    });
    const historialFiltrado = historial.filter(
      (item) =>
        !item.cambio.toLowerCase().includes("documento subido") &&
        !item.cambio.toLowerCase().includes("comunicación interna")
    );
    return docToExpediente(doc, historialFiltrado, []);
  } catch (error) {
    console.error("Error al consultar expediente:", error);
    return null;
  }
}

export async function consultarExpedienteCompleto(
  numeroExpediente: string
): Promise<Expediente | null> {
  try {
    const doc = await convexClient.query(api.expedientes.getById, {
      id: numeroExpediente,
    });
    if (!doc) return null;
    const [historial, comunicaciones] = await Promise.all([
      convexClient.query(api.historialCambios.listByExpedienteId, {
        expediente_id: doc.id,
      }),
      convexClient.query(api.comunicacionesInternas.listByExpedienteId, {
        expediente_id: doc.id,
      }),
    ]);
    return docToExpediente(doc, historial, comunicaciones);
  } catch (error) {
    console.error("Error al consultar expediente completo:", error);
    return null;
  }
}

export async function obtenerExpedientes(
  limite: number = 50,
  offset: number = 0
): Promise<Expediente[]> {
  const cacheKey = `expedientes_${limite}_${offset}`;
  const cached = cacheService.get<Expediente[]>(cacheKey);
  if (cached) return cached;
  const docs = await convexClient.query(api.expedientes.list, { limit: limite, offset });
  const result: Expediente[] = [];
  for (const doc of docs) {
    const historial = await convexClient.query(api.historialCambios.listByExpedienteId, {
      expediente_id: doc.id,
    });
    const historialParaDoc =
      historial.length > 0
        ? historial
        : [
            {
              fecha: doc.fecha_creacion,
              usuario: "Sistema",
              cambio: "Expediente registrado en el sistema",
            },
          ];
    result.push(docToExpediente(doc, historialParaDoc, []));
  }
  cacheService.set(cacheKey, result, 1 * 60 * 1000);
  return result;
}

export async function obtenerExpedientesConHistorialCompleto(): Promise<Expediente[]> {
  const cacheKey = "expedientes_completos";
  const cached = cacheService.get<Expediente[]>(cacheKey);
  if (cached) return cached;
  const docs = await convexClient.query(api.expedientes.list, { limit: 25, offset: 0 });
  const result: Expediente[] = [];
  for (const doc of docs) {
    const [historial, comunicaciones] = await Promise.all([
      convexClient.query(api.historialCambios.listByExpedienteId, {
        expediente_id: doc.id,
      }),
      convexClient.query(api.comunicacionesInternas.listByExpedienteId, {
        expediente_id: doc.id,
      }),
    ]);
    result.push(docToExpediente(doc, historial, comunicaciones));
  }
  cacheService.set(cacheKey, result, 1 * 60 * 1000);
  return result;
}

// Interfaz para las actualizaciones procesadas
export interface ActualizacionProcesada {
  fecha: string;
  usuario: string;
  cambio: string;
  informacionDenunciante?: string;
  expedienteId: string;
  tipoBarrera: string;
  estado: string;
  fechaUltimaActualizacion: string;
}

export async function obtenerUltimasActualizaciones(
  limite: number = 100
): Promise<ActualizacionProcesada[]> {
  const cacheKey = `ultimas_actualizaciones_${limite}`;
  const cached = cacheService.get<ActualizacionProcesada[]>(cacheKey);
  if (cached) {
    console.log(`✅ Cache hit para ultimas_actualizaciones_${limite}`);
    return cached;
  }

  console.log(`🔄 Cargando últimas ${limite} actualizaciones desde Convex...`);
  const result = await convexClient.query(api.historialCambios.getLatestUpdates, {
    limite,
  });

  console.log(`✅ Obtenidas ${result.length} actualizaciones desde Convex`);
  cacheService.set(cacheKey, result, 2 * 60 * 1000); // Cache por 2 minutos
  return result;
}

export async function obtenerExpedientesFiltrados(
  filtros: FiltrosExpedientes = {},
  limite: number = 20,
  offset: number = 0
): Promise<{ expedientes: Expediente[]; total: number }> {
  const busquedaTrimmed = filtros.busqueda?.trim();
  const queryArgs: Parameters<typeof convexClient.query>[1] = {
    estado: filtros.estado,
    departamento: filtros.departamento,
    tipo_barrera: filtros.tipoBarrera,
    fecha_desde: filtros.fechaDesde,
    fecha_hasta: filtros.fechaHasta,
    ordenar_por: filtros.ordenarPor,
    limite,
    offset,
  };
  if (busquedaTrimmed) (queryArgs as Record<string, unknown>).busqueda = busquedaTrimmed;
  const { expedientes: docs, total } = await convexClient.query(api.expedientes.listFiltered, queryArgs);
  const expedientes: Expediente[] = [];
  for (const doc of docs) {
    const historial = await convexClient.query(api.historialCambios.listByExpedienteId, {
      expediente_id: doc.id,
    });
    const defaultH =
      historial.length > 0
        ? historial
        : [
            {
              fecha: doc.fecha_creacion,
              usuario: "Sistema",
              cambio: "Expediente registrado",
              expediente_id: doc.id,
            },
          ];
    expedientes.push(
      docToExpediente(
        doc,
        defaultH.map((x) => ({ fecha: x.fecha, usuario: x.usuario, cambio: x.cambio, informacion_denunciante: x.informacion_denunciante }))
      )
    );
  }
  return { expedientes, total };
}

export async function obtenerConteoExpedientes(): Promise<number> {
  const cacheKey = "conteo_expedientes";
  const cached = cacheService.get<number>(cacheKey);
  if (cached != null) return cached;
  const count = await convexClient.query(api.expedientes.count, {});
  cacheService.set(cacheKey, count, 2 * 60 * 1000);
  return count;
}

export async function obtenerEstadisticasDashboard(): Promise<EstadisticasDashboard | null> {
  const cacheKey = "estadisticas_dashboard";
  const cached = cacheService.get<EstadisticasDashboard>(cacheKey);
  if (cached != null) return cached;
  try {
    const stats = await convexClient.query(api.expedientes.getDashboardStats, {});
    const result: EstadisticasDashboard = {
      total: stats.total,
      enTramite: stats.enTramite,
      rechazadas: stats.rechazadas,
      resueltas: stats.resueltas,
      arquitectonicas: stats.arquitectonicas,
      urbanisticas: stats.urbanisticas,
      transporte: stats.transporte,
      tiposBarrera: stats.tiposBarrera,
      departamentos: stats.departamentos,
    };
    cacheService.set(cacheKey, result, 2 * 60 * 1000);
    return result;
  } catch (error) {
    console.error("Error al obtener estadísticas dashboard:", error);
    return null;
  }
}

function generarIdExpediente(): string {
  const digitos = Array.from({ length: 10 }, () => Math.floor(Math.random() * 10)).join("");
  const letras = Array.from({ length: 3 }, () =>
    String.fromCharCode(65 + Math.floor(Math.random() * 26))
  ).join("");
  return `ODAT${digitos}${letras}`;
}

export async function uploadExpedienteImage(file: File): Promise<string> {
  const postUrl = await convexClient.mutation(api.expedientes.generateUploadUrl, {});
  const result = await fetch(postUrl, {
    method: "POST",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!result.ok) {
    const err = await result.text();
    throw new Error(`Error al subir imagen: ${err || result.statusText}`);
  }
  const { storageId } = (await result.json()) as { storageId?: string };
  if (!storageId) throw new Error("No se recibió storageId de la imagen");
  const url = await convexClient.query(api.expedientes.getStorageUrl, {
    storageId: storageId as import("../../convex/_generated/dataModel").Id<"_storage">,
  });
  if (!url) throw new Error("No se pudo obtener la URL de la imagen subida");
  return url;
}

export async function guardarExpediente(
  expediente: Omit<
    Expediente,
    "estado" | "fechaCreacion" | "id" | "historial" | "comunicacionesInternas"
  > & { fotografiaFile?: File }
): Promise<string> {
  const id = generarIdExpediente();
  const fechaCreacion = new Date().toISOString();

  let imagenUrl = expediente.imagenUrl ?? "";
  // Preferir archivo real → Storage; nunca persistir data: base64 en el documento.
  if (expediente.fotografiaFile) {
    imagenUrl = await uploadExpedienteImage(expediente.fotografiaFile);
  } else if (imagenUrl.startsWith("data:")) {
    // Compatibilidad: si llega base64, subirlo a Storage en lugar de incrustarlo.
    const res = await fetch(imagenUrl);
    const blob = await res.blob();
    const file = new File([blob], "foto.jpg", { type: blob.type || "image/jpeg" });
    imagenUrl = await uploadExpedienteImage(file);
  }

  let imagenBarrera = expediente.imagenBarrera ?? "";
  if (imagenBarrera.startsWith("data:")) {
    const res = await fetch(imagenBarrera);
    const blob = await res.blob();
    const file = new File([blob], "barrera.jpg", { type: blob.type || "image/jpeg" });
    imagenBarrera = await uploadExpedienteImage(file);
  }

  await convexClient.mutation(api.expedientes.create, {
    id,
    estado: "En trámite",
    fecha_creacion: fechaCreacion,
    tipo_barrera: expediente.tipoBarrera ?? "",
    tipo_barrera_detalle: expediente.tipoBarreraDetalle ?? "",
    ubicacion_barrera: expediente.ubicacionBarrera ?? "",
    email: expediente.email ?? "",
    departamento: expediente.Departamento ?? "Administrativo",
    nombre_apellidos: expediente.nombreApellidos ?? "",
    telefono_fijo: expediente.telefonoFijo ?? "",
    movil: expediente.movil ?? "",
    descripcion: expediente.descripcion ?? "",
    imagen_url: imagenUrl,
    grado_discapacidad: expediente.gradoDiscapacidad ?? "",
    tipo_discapacidad: expediente.tipoDiscapacidad ?? "",
    direccion: expediente.direccion ?? "",
    localidad: expediente.localidad ?? "",
    codigo_postal: expediente.codigoPostal ?? "",
    provincia: expediente.provincia ?? "",
    documento_identidad: expediente.documentoIdentidad ?? "",
    fecha_nacimiento: expediente.fechaNacimiento ?? "",
    genero: expediente.genero ?? "",
    baremo_movilidad: expediente.baremoMovilidad ?? "",
    tipo_persona: expediente.tipoPersona ?? "",
    porcentaje_discapacidad: expediente.porcentajeDiscapacidad ?? "",
    imagen_barrera: imagenBarrera,
  });
  await convexClient.mutation(api.historialCambios.add, {
    expediente_id: id,
    fecha: fechaCreacion,
    usuario: "Sistema",
    cambio: "Expediente registrado en el sistema",
  });
  cacheService.delete("conteo_expedientes");
  cacheService.delete("estadisticas_dashboard");
  cacheService.deletePattern("expedientes_");
  cacheService.deletePattern("estadisticas_actividad_");
  cacheService.deletePattern("denuncias_por_zona_");
  cacheService.deletePattern("ultimas_actualizaciones_");
  try {
    const { emailService } = await import("./emailService.convex");
    if (expediente.email) {
      await emailService.sendExpedienteCreatedEmail(
        expediente.email,
        id,
        expediente.tipoBarrera ?? "Sin especificar",
        expediente.ubicacionBarrera ?? "Sin especificar"
      );
    }
  } catch (e) {
    console.warn("Error al enviar email de expediente creado:", e);
  }
  return id;
}

export async function agregarComunicacionInterna(
  expedienteId: string,
  usuario: string,
  mensaje: string
): Promise<void> {
  const fechaActual = new Date().toISOString();
  await convexClient.mutation(api.comunicacionesInternas.add, {
    expediente_id: expedienteId,
    fecha: fechaActual,
    usuario,
    mensaje,
  });
  await convexClient.mutation(api.historialCambios.add, {
    expediente_id: expedienteId,
    fecha: fechaActual,
    usuario,
    cambio: "Comunicación interna agregada",
  });
  cacheService.delete("conteo_expedientes");
  cacheService.delete("estadisticas_dashboard");
  cacheService.deletePattern("expedientes_");
  cacheService.deletePattern("estadisticas_actividad_");
  cacheService.deletePattern("denuncias_por_zona_");
  cacheService.deletePattern("ultimas_actualizaciones_");
}

export async function actualizarExpediente(
  expedienteId: string,
  actualizaciones: {
    estado?: string;
    Departamento?: "Arquitecto" | "Jurídico" | "Administrativo";
    tipoBarreraDetalle?: string;
    ubicacionBarrera?: string;
  },
  usuario: string,
  informacionDenunciante?: string
): Promise<void> {
  const updates: { estado?: string; departamento?: string; tipo_barrera_detalle?: string; ubicacion_barrera?: string } = {};
  if (actualizaciones.estado !== undefined) updates.estado = actualizaciones.estado;
  if (actualizaciones.Departamento !== undefined) updates.departamento = actualizaciones.Departamento;
  if (actualizaciones.tipoBarreraDetalle !== undefined) updates.tipo_barrera_detalle = actualizaciones.tipoBarreraDetalle;
  if (actualizaciones.ubicacionBarrera !== undefined) updates.ubicacion_barrera = actualizaciones.ubicacionBarrera;
  if (Object.keys(updates).length > 0) {
    await convexClient.mutation(api.expedientes.update, {
      id: expedienteId,
      ...updates,
    });
  }
  const cambio = Object.entries(actualizaciones)
    .map(([k, v]) => `${k}: ${v ?? ""}.`)
    .join("\n");
  await convexClient.mutation(api.historialCambios.add, {
    expediente_id: expedienteId,
    fecha: new Date().toISOString(),
    usuario,
    cambio,
    informacion_denunciante: informacionDenunciante,
  });
  cacheService.delete("conteo_expedientes");
  cacheService.delete("estadisticas_dashboard");
  cacheService.deletePattern("expedientes_");
  cacheService.deletePattern("estadisticas_actividad_");
  cacheService.deletePattern("denuncias_por_zona_");
  cacheService.deletePattern("ultimas_actualizaciones_");
}

export async function eliminarExpediente(expedienteId: string): Promise<void> {
  await convexClient.mutation(api.expedientes.remove, { id: expedienteId });
  cacheService.delete("conteo_expedientes");
  cacheService.delete("estadisticas_dashboard");
  cacheService.deletePattern("expedientes_");
  cacheService.deletePattern("estadisticas_actividad_");
  cacheService.deletePattern("denuncias_por_zona_");
  cacheService.deletePattern("ultimas_actualizaciones_");
}

export async function migrateExpedientesFromFirebase(_expedientes: Expediente[]): Promise<void> {
  console.warn("Migración desde Firebase deshabilitada; se usa Convex");
}
